import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { localeCode, useLocale, useT, type Locale } from '@/shared/i18n'

const UTILITY = 'timestamp-converter'
const MS_PER_DAY = 86_400_000
const NS_PER_MS = 1_000_000n

type TimestampFormat = 'seconds' | 'milliseconds' | 'nanoseconds' | 'epochDays'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

function nowMs(): number {
  return Date.now()
}

/** Convert a UTC millisecond timestamp to a human-readable date in the given timezone. */
function formatDate(ts: number, tz: string, locale: Locale): string {
  const intlLocale = localeCode(locale)
  try {
    return new Intl.DateTimeFormat(intlLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: tz,
      timeZoneName: 'short',
    }).format(new Date(ts))
  } catch {
    return new Date(ts).toLocaleString(intlLocale)
  }
}

/** Format a UTC millisecond timestamp as ISO 8601 in the given timezone. */
function formatIso(ts: number, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(ts))

    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
    const y = get('year')
    const mo = get('month')
    const d = get('day')
    const h = get('hour')
    const mi = get('minute')
    const s = get('second')

    const tzParts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date(ts))

    const tzName = tzParts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    const offset = tzName.startsWith('GMT') ? tzName.slice(3) : 'Z'

    return `${y}-${mo}-${d}T${h}:${mi}:${s}${offset}`
  } catch {
    return '—'
  }
}

function isDateMsInCurrentRange(ms: number): boolean {
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return false
  const year = date.getUTCFullYear()
  return year >= 2000 && year <= 2100
}

function normalizeTimestamp(value: string): { seconds: number; ms: number; mode: TimestampFormat } | null {
  const num = Number(value)
  if (Number.isNaN(num) || !Number.isFinite(num)) return null

  if (isDateMsInCurrentRange(num)) {
    return { seconds: Math.floor(num / 1000), ms: num, mode: 'milliseconds' }
  }

  const secondsMs = num * 1000
  if (isDateMsInCurrentRange(secondsMs)) {
    return { seconds: num, ms: secondsMs, mode: 'seconds' }
  }

  if (/^-?\d+$/.test(value)) {
    const bigintValue = BigInt(value)
    const digits = value.replace(/^-/, '').length
    const nanosMs = Number(bigintValue / NS_PER_MS)
    if (isDateMsInCurrentRange(nanosMs)) {
      return { seconds: Math.floor(nanosMs / 1000), ms: nanosMs, mode: 'nanoseconds' }
    }

    const epochDaysMs = Number(bigintValue * BigInt(MS_PER_DAY))
    if (isDateMsInCurrentRange(epochDaysMs)) {
      return { seconds: Math.floor(epochDaysMs / 1000), ms: epochDaysMs, mode: 'epochDays' }
    }

    if (digits >= 18) {
      return { seconds: Math.floor(nanosMs / 1000), ms: nanosMs, mode: 'nanoseconds' }
    }

    if (digits >= 13 && digits <= 15) {
      return { seconds: Math.floor(num / 1000), ms: num, mode: 'milliseconds' }
    }
  }

  return { seconds: num, ms: secondsMs, mode: 'seconds' }
}

function formatNanoseconds(ms: number): string {
  return String(BigInt(Math.floor(ms)) * NS_PER_MS)
}

function formatEpochDays(ms: number): string {
  return String(Math.floor(ms / MS_PER_DAY))
}

/** Fallback timezone list for older browsers without Intl.supportedValuesOf. */
function getAllTimezones(): string[] {
  try {
    // Modern API — available in Node 19+, most modern browsers
    return Intl.supportedValuesOf('timeZone')
  } catch {
    // Fallback: cover the major UTC-offset zones plus common aliases.
    // This is a curated subset; the full IANA db has ~600 entries.
    return [
      'UTC',
      'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos',
      'America/Argentina/Buenos_Aires',
      'America/Bogota', 'America/Caracas', 'America/Chicago',
      'America/Denver', 'America/Halifax', 'America/Lima',
      'America/Los_Angeles', 'America/Mexico_City', 'America/New_York',
      'America/Phoenix', 'America/Santiago', 'America/Sao_Paulo',
      'America/St_Johns', 'America/Toronto', 'America/Vancouver',
      'America/Winnipeg',
      'Asia/Almaty', 'Asia/Bangkok', 'Asia/Dhaka', 'Asia/Dubai',
      'Asia/Ho_Chi_Minh', 'Asia/Hong_Kong', 'Asia/Jakarta',
      'Asia/Karachi', 'Asia/Kathmandu', 'Asia/Kolkata',
      'Asia/Krasnoyarsk', 'Asia/Magadan', 'Asia/Manila',
      'Asia/Novosibirsk', 'Asia/Riyadh', 'Asia/Seoul',
      'Asia/Shanghai', 'Asia/Singapore', 'Asia/Taipei',
      'Asia/Tashkent', 'Asia/Tbilisi', 'Asia/Tehran',
      'Asia/Tokyo', 'Asia/Vladivostok', 'Asia/Yakutsk',
      'Asia/Yekaterinburg', 'Asia/Yerevan',
      'Atlantic/Azores', 'Atlantic/Cape_Verde',
      'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin',
      'Australia/Eucla', 'Australia/Hobart', 'Australia/Lord_Howe',
      'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
      'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin',
      'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest',
      'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Helsinki',
      'Europe/Istanbul', 'Europe/Kaliningrad', 'Europe/Kyiv',
      'Europe/Lisbon', 'Europe/London', 'Europe/Madrid',
      'Europe/Minsk', 'Europe/Moscow', 'Europe/Oslo',
      'Europe/Paris', 'Europe/Prague', 'Europe/Riga',
      'Europe/Rome', 'Europe/Samara', 'Europe/Sofia',
      'Europe/Stockholm', 'Europe/Tallinn', 'Europe/Vienna',
      'Europe/Vilnius', 'Europe/Warsaw', 'Europe/Zurich',
      'Indian/Cocos', 'Pacific/Auckland', 'Pacific/Chatham',
      'Pacific/Easter', 'Pacific/Fiji', 'Pacific/Galapagos',
      'Pacific/Gambier', 'Pacific/Guam', 'Pacific/Honolulu',
      'Pacific/Kiritimati', 'Pacific/Majuro', 'Pacific/Marquesas',
      'Pacific/Norfolk', 'Pacific/Noumea', 'Pacific/Pago_Pago',
      'Pacific/Pitcairn', 'Pacific/Port_Moresby', 'Pacific/Tahiti',
      'Pacific/Tarawa', 'Pacific/Tongatapu', 'Pacific/Wake',
      'Pacific/Wallis',
    ]
  }
}

function dateTimeToTimestampInTz(dateStr: string, timezone: string): number {
  const [datePart = '', timePart = '00:00'] = dateStr.split('T')
  const [y = 0, m = 1, d = 1] = datePart.split('-').map(Number)
  const [h = 0, min = 0] = timePart.split(':').map(Number)

  let ts = Date.UTC(y, m - 1, d, h, min)

  for (let i = 0; i < 2; i++) {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date(ts))

    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    const match = tzName.match(/GMT([+-])(\d{2}):(\d{2})/)
    if (match) {
      const totalMin = parseInt(match[2]!, 10) * 60 + parseInt(match[3]!, 10)
      const adjustment = match[1]! === '+' ? -totalMin : totalMin
      ts = Date.UTC(y, m - 1, d, h, min) + adjustment * 60000
    } else {
      break
    }
  }

  return ts
}

// ─── Live clock ────────────────────────────────────────────────────────────────

function useNow(): number {
  const [now, setNow] = useState(nowMs)

  useEffect(() => {
    const id = setInterval(() => setNow(nowMs()), 32)
    return () => clearInterval(id)
  }, [])

  return now
}

// ─── Sub-components ────────────────────────────────────────────────────────────

interface TimestampFieldProps {
  label: string
  value: string
  copyLabel: string
  copiedLabel: string
  copyAriaPrefix: string
  onCopy?: () => void
}

function TimestampField({
  label,
  value,
  copyLabel,
  copiedLabel,
  copyAriaPrefix,
  onCopy,
}: TimestampFieldProps) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
    onCopy?.()
  }, [value, onCopy])
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="mt-0.5 font-mono text-sm font-medium break-all">{value}</div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="ml-2 h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
          aria-label={copied ? copiedLabel : `${copyAriaPrefix}: ${label}`}
          title={copied ? copiedLabel : copyLabel}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </Button>
      </CardContent>
    </Card>
  )
}

interface LiveTimestampCardProps {
  now: number
  timezone: string
  locale: Locale
  title: string
  secondsLabel: string
  millisecondsLabel: string
  nanosecondsLabel: string
  epochDaysLabel: string
  copyLabel: string
  copiedLabel: string
  onCopy?: () => void
}

function LiveTimestampCard({
  now,
  timezone,
  locale,
  title,
  secondsLabel,
  millisecondsLabel,
  nanosecondsLabel,
  epochDaysLabel,
  copyLabel,
  copiedLabel,
  onCopy,
}: LiveTimestampCardProps) {
  const seconds = Math.floor(now / 1000)
  const milliseconds = String(now % 1000).padStart(3, '0')
  const nanoseconds = formatNanoseconds(now)
  const epochDays = formatEpochDays(now)
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(String(now)).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
    onCopy?.()
  }, [now, onCopy])

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-muted-foreground">
              {title}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              {formatDate(now, timezone, locale)}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={handleCopy}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? copiedLabel : copyLabel}
          </Button>
        </div>

        <div className="rounded-md border bg-muted/30 p-3">
          <div className="flex min-w-0 items-baseline font-mono tabular-nums">
            <span className="min-w-0 break-all text-2xl font-semibold leading-none sm:text-4xl">
              {seconds}
            </span>
            <span className="mx-1 text-2xl font-semibold leading-none text-muted-foreground sm:text-4xl">
              .
            </span>
            <span className="rounded-md bg-primary px-2 py-1 text-xl font-semibold leading-none text-primary-foreground shadow-sm sm:text-3xl">
              {milliseconds}
            </span>
          </div>
          <div className="mt-2 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <div>
              {secondsLabel}: <span className="font-mono text-foreground">{seconds}</span>
            </div>
            <div>
              {millisecondsLabel}: <span className="font-mono text-foreground">{now}</span>
            </div>
            <div>
              {nanosecondsLabel}:{' '}
              <span className="break-all font-mono text-foreground">{nanoseconds}</span>
            </div>
            <div>
              {epochDaysLabel}: <span className="font-mono text-foreground">{epochDays}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function CopyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function TimestampPage() {
  const t = useT()
  const { locale } = useLocale()
  const now = useNow()

  const [inputValue, setInputValue] = useState('')
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
  )
  const [invoked, setInvoked] = useState(false)

  useEffect(() => {
    if (!invoked && inputValue.length > 0) {
      setInvoked(true)
      toolInvoke(UTILITY)
    }
  }, [inputValue, invoked])

  const parsedTimestamp = useMemo(() => {
    const trimmed = inputValue.trim()
    if (!trimmed) return null
    return normalizeTimestamp(trimmed)
  }, [inputValue])

  const dateToTimestamp = useMemo(() => {
    const trimmed = inputValue.trim()
    if (!trimmed) return null
    if (trimmed.includes('T')) {
      const ms = dateTimeToTimestampInTz(trimmed, timezone)
      if (Number.isNaN(ms)) return null
      return {
        seconds: String(Math.floor(ms / 1000)),
        milliseconds: String(ms),
        nanoseconds: formatNanoseconds(ms),
        epochDays: formatEpochDays(ms),
      }
    }
    const num = Number(trimmed)
    if (Number.isNaN(num)) return null
    return {
      seconds: String(Math.floor(num)),
      milliseconds: String(Math.floor(num)),
      nanoseconds: formatNanoseconds(num),
      epochDays: formatEpochDays(num),
    }
  }, [inputValue, timezone])

  const handleNowClick = useCallback(() => {
    setInputValue(String(nowSeconds()))
    setInvoked(true)
    toolInvoke(UTILITY)
  }, [])

  const handleTsInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }, [])

  const handleDateInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }, [])

  const handleTimezoneChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTimezone(e.target.value)
  }, [])

  const isNumericInput = inputValue.trim() && /^-?\d+(\.\d+)?$/.test(inputValue.trim())

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6">
        <div className="mb-2 inline-flex rounded-md border px-2 py-1 text-xs text-muted-foreground">
          {t.common.localBadge}
        </div>
        <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
          {t.timestamp.title}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t.timestamp.description}
        </p>
      </div>

      {/* Live now — always visible */}
      <LiveTimestampCard
        now={now}
        timezone={timezone}
        locale={locale}
        title={t.timestamp.currentUnixTime}
        secondsLabel={t.timestamp.seconds}
        millisecondsLabel={t.timestamp.milliseconds}
        nanosecondsLabel={t.timestamp.nanoseconds}
        epochDaysLabel={t.timestamp.epochDays}
        copyLabel={t.common.copy}
        copiedLabel={t.common.copied}
        onCopy={() => toolInvoke(UTILITY)}
      />

      {/* Timestamp to date */}
      <div className="mt-6 space-y-3">
        <label htmlFor="ts-input" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {t.timestamp.timestampToDate}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            id="ts-input"
            type="text"
            inputMode="numeric"
            placeholder={t.timestamp.inputPlaceholder}
            value={isNumericInput || !inputValue ? inputValue : ''}
            onChange={handleTsInputChange}
            className={cn(
              'flex h-11 w-full min-w-0 rounded-xl border bg-background px-3 py-2 sm:h-10',
              'text-base font-mono file:border-0 file:bg-transparent file:text-sm file:font-medium sm:text-sm',
              'placeholder:text-muted-foreground focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50',
            )}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleNowClick}
            className="h-11 w-full shrink-0 sm:h-10 sm:w-auto"
          >
            {t.timestamp.now}
          </Button>
        </div>

        {parsedTimestamp && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              {t.timestamp.detectedFormat}:{' '}
              {
                {
                  seconds: t.timestamp.secondsFormat,
                  milliseconds: t.timestamp.millisecondsFormat,
                  nanoseconds: t.timestamp.nanosecondsFormat,
                  epochDays: t.timestamp.epochDaysFormat,
                }[parsedTimestamp.mode]
              }
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <TimestampField
                label={t.timestamp.seconds}
                value={String(parsedTimestamp.seconds)}
                copyLabel={t.common.copy}
                copiedLabel={t.common.copied}
                copyAriaPrefix={t.timestamp.copyAriaPrefix}
              />
              <TimestampField
                label={t.timestamp.milliseconds}
                value={String(parsedTimestamp.ms)}
                copyLabel={t.common.copy}
                copiedLabel={t.common.copied}
                copyAriaPrefix={t.timestamp.copyAriaPrefix}
              />
              <TimestampField
                label={t.timestamp.nanoseconds}
                value={formatNanoseconds(parsedTimestamp.ms)}
                copyLabel={t.common.copy}
                copiedLabel={t.common.copied}
                copyAriaPrefix={t.timestamp.copyAriaPrefix}
              />
              <TimestampField
                label={t.timestamp.epochDays}
                value={formatEpochDays(parsedTimestamp.ms)}
                copyLabel={t.common.copy}
                copiedLabel={t.common.copied}
                copyAriaPrefix={t.timestamp.copyAriaPrefix}
              />
            </div>
            <TimestampField
              label={t.timestamp.dateTime}
              value={formatDate(parsedTimestamp.ms, timezone, locale)}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
            <TimestampField
              label="ISO 8601"
              value={formatIso(parsedTimestamp.ms, timezone)}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
          </div>
        )}
      </div>

      {/* Date to timestamp */}
      <div className="mt-6 space-y-3">
        <label htmlFor="date-input" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {t.timestamp.dateToTimestamp}
        </label>
        <input
          id="date-input"
          type="datetime-local"
          onChange={handleDateInputChange}
          className={cn(
            'flex h-11 w-full min-w-0 rounded-xl border bg-background px-3 py-2 sm:h-10',
            'text-base font-mono file:border-0 file:bg-transparent file:text-sm file:font-medium sm:text-sm',
            'placeholder:text-muted-foreground focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />

        {inputValue.trim().includes('T') && dateToTimestamp && (
          <div className="grid gap-2 sm:grid-cols-2">
            <TimestampField
              label={t.timestamp.seconds}
              value={dateToTimestamp.seconds}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
            <TimestampField
              label={t.timestamp.milliseconds}
              value={dateToTimestamp.milliseconds}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
            <TimestampField
              label={t.timestamp.nanoseconds}
              value={dateToTimestamp.nanoseconds}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
            <TimestampField
              label={t.timestamp.epochDays}
              value={dateToTimestamp.epochDays}
              copyLabel={t.common.copy}
              copiedLabel={t.common.copied}
              copyAriaPrefix={t.timestamp.copyAriaPrefix}
            />
          </div>
        )}
      </div>

      {/* Timezone selector */}
      <div className="mt-6 space-y-2">
        <label htmlFor="tz-select" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {t.timestamp.timezone}
        </label>
        <select
          id="tz-select"
          value={timezone}
          onChange={handleTimezoneChange}
          className={cn(
            'w-full rounded-xl border bg-background px-3 py-2',
            'text-sm focus:outline-none focus:ring-2 focus:ring-primary/30',
          )}
        >
          {getAllTimezones().map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </div>

      {/* FAQ */}
      <section className="mt-10 space-y-5 border-t pt-8">
        {t.timestamp.faq.map((item) => (
          <div key={item.title}>
            <h2 className="text-xl font-semibold tracking-normal">{item.title}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              {item.body}
            </p>
          </div>
        ))}
      </section>
    </div>
  )
}
